import { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Close as ClearIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import PropertyCard from "./PropertyCard";

// ---- presentational helpers ----

const EmptyState = ({ message }) => (
  <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
    <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{message}</Typography>
  </Box>
);

// ---- component ----

const PropertyListPanel = ({
  loading,
  error,
  properties,
  total,
  page,
  totalPages,
  canPrevious,
  canNext,
  onPrevious,
  onNext,
  selectedId,
  selectedIds = [],
  onSelect,
  onOpenDetails,
  onAdd,
  onIngest,
  onToggleSelect,
  activeFilters,
  linkedContactFilter,
  onClearLinkedContactFilter,
  domainFilter,
  onDomainFilterChange,
  categories = [],
  availableStatuses = [],
  onStatusChange,
  onCategoryChange,
  onDeleteProperty,
  onCopyProperty,
  pageSize = 25,
  categoryFilter = "",
  onCategoryFilterChange,
  propertyStatusFilter = "",
  onPropertyStatusFilterChange,
  propertyStateFilter = "",
  onPropertyStateFilterChange,
  availablePropertyStates = [],
  propertyStateCounts,
}) => {
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [categoryTarget, setCategoryTarget] = useState(null);
  const [busyIds, setBusyIds] = useState({});

  const activeCategories = useMemo(
    () => (Array.isArray(categories) ? categories.filter((c) => c?.isActive !== false) : []),
    [categories]
  );

  const openStatusPicker = (e, property) => {
    e.stopPropagation();
    if (!property?.id || busyIds[property.id]) return;
    setStatusTarget(property);
    setStatusAnchor(e.currentTarget);
  };

  const openCategoryPicker = (e, property) => {
    e.stopPropagation();
    if (!property?.id || busyIds[property.id]) return;
    setCategoryTarget(property);
    setCategoryAnchor(e.currentTarget);
  };

  const handleStatusPick = async (statusValue) => {
    if (!statusTarget || !onStatusChange) return;
    const target = statusTarget;
    setBusyIds((p) => ({ ...p, [target.id]: true }));
    setStatusAnchor(null);
    try {
      await onStatusChange(target, statusValue);
    } finally {
      setBusyIds((p) => ({ ...p, [target.id]: false }));
      setStatusTarget(null);
    }
  };

  const handleCategoryPick = async (category) => {
    if (!categoryTarget || !onCategoryChange) return;
    const target = categoryTarget;
    setBusyIds((p) => ({ ...p, [target.id]: true }));
    setCategoryAnchor(null);
    try {
      await onCategoryChange(target, category);
    } finally {
      setBusyIds((p) => ({ ...p, [target.id]: false }));
      setCategoryTarget(null);
    }
  };

  const handleDeleteClick = async (e, property) => {
    e.stopPropagation();
    if (!onDeleteProperty || !property?.id) return;
    const confirmed = window.confirm(
      `Delete property "${property?.name || "Unnamed Property"}"?`
    );
    if (!confirmed) return;
    setBusyIds((p) => ({ ...p, [`delete-${property.id}`]: true }));
    try {
      await onDeleteProperty(property);
    } finally {
      setBusyIds((p) => ({ ...p, [`delete-${property.id}`]: false }));
    }
  };

  const handleCopyClick = async (e, property) => {
    e.stopPropagation();
    if (!onCopyProperty || !property?.id) return;
    setBusyIds((p) => ({ ...p, [`copy-${property.id}`]: true }));
    try {
      await onCopyProperty(property);
    } finally {
      setBusyIds((p) => ({ ...p, [`copy-${property.id}`]: false }));
    }
  };

  const safePage = Math.max(1, Number(page) || 1);
  const propertyNumber = (idx) => (safePage - 1) * pageSize + idx + 1;

  return (
    <AddressbookPanelShell
      scrollResetKey={page}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          {onCategoryFilterChange && (
            <TextField
              select
              size="small"
              value={categoryFilter || ""}
              onChange={(e) => onCategoryFilterChange(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return "All Categories";
                  return String(selected);
                },
              }}
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  height: 26,
                  fontSize: 12,
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  "& fieldset": { borderColor: "#D1D5DB" },
                  "&:hover fieldset": { borderColor: "#9CA3AF" },
                },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                <em>All Categories</em>
              </MenuItem>
              {activeCategories.map((c) => {
                const value = c?.value || c?.id || c?.name || c?.label || "";
                const label = c?.label || c?.name || c?.value || c?.id || "";
                if (!value || !label) return null;
                return (
                  <MenuItem key={`filter-cat-${value}`} value={value} sx={{ fontSize: 12 }}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
          {onPropertyStateFilterChange && (
            <TextField
              select
              size="small"
              value={propertyStateFilter || ""}
              onChange={(e) => onPropertyStateFilterChange(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return "All Systems";
                  return String(selected);
                },
              }}
              sx={{
                minWidth: 130,
                "& .MuiOutlinedInput-root": {
                  height: 26,
                  fontSize: 12,
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  "& fieldset": { borderColor: "#D1D5DB" },
                  "&:hover fieldset": { borderColor: "#9CA3AF" },
                },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                <em>All Systems</em>
              </MenuItem>
              {Array.from(
                new Set(
                  (Array.isArray(availablePropertyStates) ? availablePropertyStates : []).map((stateEntry) =>
                    String(
                      typeof stateEntry === "string"
                        ? stateEntry
                        : stateEntry?.value || stateEntry?.name || stateEntry?.label || stateEntry?.id || ""
                  ).trim()
                  ).filter(Boolean)
                )
              )
                .filter((stateValue) => {
                  if (stateValue === "no_state") return false;
                  return (propertyStateCounts?.[stateValue] || 0) > 0;
                })
                .map((stateValue, idx) => {
                  const count = propertyStateCounts ? propertyStateCounts[stateValue] : null;
                  return (
                    <MenuItem key={`filter-state-${stateValue}-${idx}`} value={stateValue} sx={{ fontSize: 12 }}>
                      {stateValue}{count != null ? ` (${count})` : ""}
                    </MenuItem>
                  );
                })}
            </TextField>
          )}
          {onPropertyStatusFilterChange && (
            <TextField
              select
              size="small"
              value={propertyStatusFilter || ""}
              onChange={(e) => onPropertyStatusFilterChange(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return "All Statuses";
                  return String(selected);
                },
              }}
              sx={{
                minWidth: 140,
                "& .MuiOutlinedInput-root": {
                  height: 26,
                  fontSize: 12,
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  "& fieldset": { borderColor: "#D1D5DB" },
                  "&:hover fieldset": { borderColor: "#9CA3AF" },
                },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                <em>All Statuses</em>
              </MenuItem>
              {availableStatuses.map((statusValue, idx) => {
                const value = String(statusValue || "").trim();
                if (!value) return null;
                return (
                  <MenuItem key={`filter-status-${value}-${idx}`} value={value} sx={{ fontSize: 12 }}>
                    {value}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
          {onDomainFilterChange && (
            <TextField
              size="small"
              placeholder="Domain…"
              value={domainFilter || ""}
              onChange={(e) => onDomainFilterChange(e.target.value)}
              InputProps={{
                endAdornment: domainFilter ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onDomainFilterChange("")} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: 120,
                "& .MuiOutlinedInput-root": {
                  height: 26,
                  fontSize: 12,
                  borderRadius: 1,
                  "& fieldset": { borderColor: "#D1D5DB" },
                  "&:hover fieldset": { borderColor: "#9CA3AF" },
                  "&.Mui-focused fieldset": { borderColor: "#3B82F6", borderWidth: 1.5 },
                },
              }}
            />
          )}
        </Box>
      }
      headerActions={
        <>
          {onIngest ? (
            <Tooltip title="Ingest file">
              <IconButton
                size="small"
                aria-label="Ingest file"
                onClick={onIngest}
                sx={{ backgroundColor: "#3F51B5", "&:hover": { backgroundColor: "#3949AB" }, width: 24, height: 24, p: 0 }}
              >
                <UploadFileIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {onAdd ? (
            <IconButton
              size="small"
              onClick={onAdd}
              title="Add property"
              sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}
            >
              <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
            </IconButton>
          ) : null}
        </>
      }
      collectionLabel="Properties"
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {properties.length} of {total}
        </Typography>
      }
      footerRight={
        <>
          <IconButton size="small" onClick={onPrevious} disabled={!canPrevious} aria-label="Previous page">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography sx={{ color: "text.secondary", fontSize: 11, minWidth: 72, textAlign: "center" }}>
            {page} / {totalPages}
          </Typography>
          <IconButton size="small" onClick={onNext} disabled={!canNext} aria-label="Next page">
            <ChevronRight fontSize="small" />
          </IconButton>
        </>
      }
    >
      {linkedContactFilter ? (
        <Box
          sx={{
            mb: 1,
            px: 1,
            py: 0.6,
            borderRadius: 1.25,
            backgroundColor: "#EEF2FF",
            border: "1px solid #C7D2FE",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Typography sx={{ fontSize: 11, color: "#3730A3", lineHeight: 1.35, flex: 1 }}>
            Showing {linkedContactFilter.propertyIds?.length || 0} {linkedContactFilter.propertyIds?.length === 1 ? "property" : "properties"} linked to <strong>{linkedContactFilter.contactName}</strong>
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => onClearLinkedContactFilter?.()}
            sx={{
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 700,
              color: "#3730A3",
              backgroundColor: "transparent",
              border: "1px solid #C7D2FE",
              borderRadius: 999,
              px: 1,
              py: 0.15,
              "&:hover": { backgroundColor: "#E0E7FF" },
            }}
          >
            Clear
          </Box>
        </Box>
      ) : null}
      {!loading && !error && properties.length === 0 && Array.isArray(activeFilters) && activeFilters.length > 0 && (
        <Box
          sx={{
            mb: 1.25,
            px: 1,
            py: 0.75,
            borderRadius: 1.25,
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
          }}
        >
          <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.35 }}>
            Active filters: {activeFilters.join(" | ")}
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center", gap: 1.25 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Loading properties...</Typography>
        </Box>
      )}

      {!loading && error && <EmptyState message={error} />}
      {!loading && !error && properties.length === 0 && (
        <EmptyState message="No properties in this collection yet." />
      )}

      {!loading && !error && properties.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {properties.map((property, idx) => (
            <PropertyCard
              key={property.id || `card-${idx}`}
              property={property}
              number={propertyNumber(idx)}
              categories={categories}
              isSelected={selectedId === property.id}
              isChecked={selectedIds.includes(property.id)}
              onSelect={onSelect}
              onOpenDetails={onOpenDetails}
              onToggleSelect={onToggleSelect}
              onOpenStatusPicker={openStatusPicker}
              onOpenCategoryPicker={onCategoryChange ? openCategoryPicker : undefined}
              onCopy={onCopyProperty ? handleCopyClick : undefined}
              onDelete={onDeleteProperty ? handleDeleteClick : undefined}
              isStatusBusy={Boolean(busyIds[property.id])}
              isDeleting={Boolean(busyIds[`delete-${property.id}`])}
              isCopying={Boolean(busyIds[`copy-${property.id}`])}
            />
          ))}
        </Box>
      )}

      {/* Status picker popover */}
      <Popover
        open={Boolean(statusAnchor)}
        anchorEl={statusAnchor}
        onClose={() => {
          setStatusAnchor(null);
          setStatusTarget(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ minWidth: 200, p: 0.75 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "#475569",
              px: 0.75,
              py: 0.5,
              borderBottom: "1px solid #E2E8F0",
              mb: 0.5,
            }}
          >
            Change Property Status
          </Typography>
          {availableStatuses.map((s) => {
            const selected = (statusTarget?.status || "").toLowerCase() === String(s).toLowerCase();
            return (
              <Box
                key={`status-${s}`}
                onClick={() => handleStatusPick(s)}
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  fontSize: 12.5,
                  fontWeight: selected ? 700 : 500,
                  color: selected ? "#1D4ED8" : "#334155",
                  backgroundColor: selected ? "#EFF6FF" : "transparent",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: selected ? "#DBEAFE" : "#F8FAFC" },
                }}
              >
                {s}
              </Box>
            );
          })}
        </Box>
      </Popover>

      {/* Category picker popover */}
      <Popover
        open={Boolean(categoryAnchor)}
        anchorEl={categoryAnchor}
        onClose={() => {
          setCategoryAnchor(null);
          setCategoryTarget(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ minWidth: 200, p: 0.75 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "#475569",
              px: 0.75,
              py: 0.5,
              borderBottom: "1px solid #E2E8F0",
              mb: 0.5,
            }}
          >
            Change Category
          </Typography>
          {activeCategories.map((c) => {
            const selected = c?.id === categoryTarget?.category;
            return (
              <Box
                key={`cat-${c.id}`}
                onClick={() => handleCategoryPick(c)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  fontSize: 12.5,
                  fontWeight: selected ? 700 : 500,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F8FAFC" },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: c.bgColor || "#9CA3AF",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
                <Typography sx={{ fontSize: 12.5 }}>{c.label || c.name || c.id}</Typography>
              </Box>
            );
          })}
          {activeCategories.length === 0 && (
            <Typography sx={{ px: 1, py: 0.75, fontSize: 12, color: "#64748B" }}>
              No active categories.
            </Typography>
          )}
        </Box>
      </Popover>
    </AddressbookPanelShell>
  );
};

export default PropertyListPanel;
